import React from 'react'
import moment from 'moment-timezone'
import DatePicker from 'antd/es/date-picker'
import { momentLocalDateFormat, momentLocalTimeFormat, range } from 'libs/utils'
import BaseField from './BaseField'


export default function FieldDatetimeRange(props) {
  /* Input Datetime
   * Expected Props:
   * className = <string> Optional
   * label = <string> Required
   * required = <boolean> Optional
   * name = <string> Required
   * value = <string> Required
   * value2 = <string> Required
   * placeholder = <string> Optional
   * valid = <boolean> Optional
   * validMessage = <string> Optional
   * invalidMessage = <string> Optional
   * help = <string> Optional
   * onChange = <function()> Required
   * onChange2 = <function()> Required
   * onBlur = <function()> Optional
   * onBlur2 = <function()> Optional
   * disabled = <boolean> Optional
   * readOnly = <boolean> Optional
   * prepend = <string> Optional
   * append = <string> Optional
   * timezone = <timezone> Optional
   * horizontal = <boolean> Optional
   * horizontalLabelSize = <int [1-23]> Optional (Default = 6)
   * minuteStep = <int> Optional (Default = 5)
   */
  const useTime = !props.type?.endsWith("date_range")
  const ignoreOffset = props.type === "date_range"
  const toString = value => (useTime ? value?.seconds(0).milliseconds(0).toISOString() : value?.toISOString(ignoreOffset).substr(0, 10)) || null
  const fromString = value => !value ? "" : props.timezone ? moment.tz(value, props.timezone) : moment(value)

  // Memos
  const timezoneAbbr = React.useMemo(() => {
    if (!props.timezone || !useTime) return ""
    const newTimezoneOffset = moment.tz(props.timezone).utcOffset()
    return newTimezoneOffset !== moment().utcOffset() ? ` [GMT${newTimezoneOffset >= 0 ? "+" : ""}${(newTimezoneOffset / 60).toFixed(2)}]` : ""
  }, [props.timezone])

  const startDate = React.useMemo(() => fromString(props.value), [props.value, props.timezone])

  const endDate = React.useMemo(() => fromString(props.value2), [props.value2, props.timezone])

  const minDate = React.useMemo(() => fromString(props.min), [props.min, props.timezone])

  const maxDate = React.useMemo(() => fromString(props.max), [props.max, props.timezone])

  // Functions
  const handleChange = values => {
    const [nextStartDate, nextEndDate] = values || []
    if (props.timezone) {
      nextStartDate?.tz(props.timezone, true)
      nextEndDate?.tz(props.timezone, true)
    }
    props.onChange({ target: { value: toString(nextStartDate) } })
    props.onChange2({ target: { value: toString(nextEndDate) } })
  }

  // - Check min/max date
  const disabledDate = (date) => {
    return (minDate && date < minDate) || (maxDate && date > maxDate)
  }

  // - Check min/max time
  const getDateValue = (date) => date ? Number(date.toISOString(true).slice(0, 10)) : null
  const getHourValue = (date) => date ? Number(date.toISOString(true).slice(11, 13)) : null
  const getMinuteValue = (date) => date ? Number(date.toISOString(true).slice(14, 16)) : null
  const disabledTime = (date) => {
    return {
      disabledHours: () => {
        if (!date) return []
        const minHour = minDate && getDateValue(date) === getDateValue(minDate) ? getHourValue(minDate) : 0
        const maxHour = maxDate && getDateValue(date) === getDateValue(maxDate) ? getHourValue(maxDate) : 23
        return range(0, 23).filter(h => h < minHour || h > maxHour)
      },
      disabledMinutes: (hour) => {
        if (!date) return []
        if (hour < 0) return range(0, 59)
        const minMinute = minDate && getDateValue(date) === getDateValue(minDate) &&
          getHourValue(date) === getHourValue(minDate) ? getMinuteValue(minDate) : 0
        const maxMinute = maxDate && getDateValue(date) === getDateValue(maxDate) &&
          getHourValue(date) === getHourValue(maxDate) ? getMinuteValue(maxDate) : 59
        return range(0, 59).filter(m => m < minMinute || m > maxMinute)
      },
    }
  }

  // Render
  return (
    <BaseField
      className={props.className}
      labelClassName={props.labelClassName}
      inputClassName={props.inputClassName}
      label={props.label}
      input={
        <DatePicker.RangePicker
          ref={props.inputRef}
          className={"form-control custom-field-input" + (
            props.valid === true ? " is-valid" : props.valid === false ? " is-invalid" : ""
          )}
          showTime={useTime && { format: momentLocalTimeFormat, minuteStep: props.minuteStep || 5 }}
          format={momentLocalDateFormat + (useTime ? ` @ ${momentLocalTimeFormat}${timezoneAbbr}` : "")}
          placeholder={props.placeholder}
          value={[startDate, endDate]}
          onChange={handleChange}
          onBlur={props.onBlur}
          disabled={props.disabled || props.readOnly}
          inputReadOnly={props.readOnly}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          autoFocus={props.autoFocus}
        />
      }
      prepend={props.prepend}
      append={props.append}
      disabled={props.disabled}
      readOnly={props.readOnly}
      required={props.required}
      valid={props.valid}
      validMessage={props.validMessage}
      invalidMessage={props.invalidMessage}
      help={props.help}
      helpAboveInput={props.helpAboveInput}
      horizontal={props.horizontal}
      horizontalLabelSize={props.horizontalLabelSize}
    />
  )
}
